import { DOCUMENT, inject, Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { LogsService } from '@services/logs';
import { isRelAllowedMultipleTimes } from '@services/seo/config';
import { JsonLD, Seo } from '@services/seo/model';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  readonly canonical = signal<string>(null!);
  readonly #jsonLdScriptList = new Set<string>();
  readonly #titleService = inject(Title);
  readonly #metaService = inject(Meta);
  readonly #renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);
  readonly #document = inject(DOCUMENT);
  readonly #logsService = inject(LogsService);

  setSeo(seo: Seo): void {
    this.#updateMetaTag(seo);
    this.updateTitle(seo.title);
  }

  updateTitle(title: string) {
    this.#titleService.setTitle(title);
    this.#metaService.updateTag({
      name: 'title',
      content: title,
    });
  }

  getTitle(): string {
    return this.#titleService.getTitle();
  }

  /**
   * Updates or sets the canonical link for the current document by appending the provided pathname to the current URL.
   *
   * @param {string} pathname - The pathname to append to the current URL as the canonical link.
   */
  setCanonicalLink(pathname: string): void {
    const url = (pathname.length > 0 ? `${environment.hostname}/${pathname}` : environment.hostname).replace(
      /([^:]\/)\/+/g,
      '$1'
    );
    this.canonical.set(url);
    this.setLinkTag('canonical', url);
  }

  /**
   * Sets a preconnect link with the specified URL to optimize network requests by establishing early connections.
   *
   * @param {string} url - The URL to which the browser should preconnect.
   */
  setPreconnectLink(url: string): void {
    this.setLinkTag('preconnect', url);
  }

  /**
   * Adds or updates a JSON-LD script element in the document's head.
   * If a script with the same ID already exists, it will be replaced with the new content.
   *
   * @param id - The unique identifier for the script element.
   * @param content - The JSON-LD content to inject.
   */
  addJsonLdScript(id: string, content: JsonLD): void {
    // Validate the input params
    if (!id?.trim()) {
      this.#logsService.error('SeoService', 'JSON-LD injection canceled : empty or invalid id.', { id, content });
      return;
    }

    let json: string | undefined;
    try {
      json = JSON.stringify(content);
    } catch (e) {
      this.#logsService.error('SeoService', `JSON.stringify failed for "${id}"`, e, content);
      return;
    }

    if (!json || json === 'undefined' || json === '{}' || json === 'null') {
      this.#logsService.warn('SeoService', `JSON-LD "${id}" ignored (empty or undefined).`, content);
      return;
    }

    // A CMS value holding "</script>" would close the tag in the prerendered HTML; JSON.parse reads the escape back as "<".
    json = json.replace(/</g, '\\u003c');

    this.#jsonLdScriptList.add(id);

    // Check if a script element with the given ID already exists
    const existingScript = this.#document?.querySelector<HTMLScriptElement>(`script#${id}`);

    if (existingScript) {
      // Update the existing script's content
      this.#renderer.setProperty(existingScript, 'textContent', json);
    } else {
      // Create a new <script> element
      const jsonLdScript = this.#renderer.createElement('script') as HTMLScriptElement;
      this.#renderer.setAttribute(jsonLdScript, 'type', 'application/ld+json');
      this.#renderer.setAttribute(jsonLdScript, 'id', id);
      this.#renderer.setProperty(jsonLdScript, 'textContent', json);

      // Safely append the new script element to the <head>
      const headElement = this.#document?.head;
      if (!headElement) {
        throw new Error('Unable to access the document head.');
      }

      this.#renderer.appendChild(headElement, jsonLdScript);
    }
  }

  /**
   * Removes a JSON-LD script element from the document's head by its ID.
   *
   * @param id - The unique identifier for the script element to remove.
   */
  removeJsonLdScript(id: string): void {
    // Validate the input param
    if (!id || !id.trim()) {
      throw new Error(`Invalid id attribute: "${id}"`);
    }

    // Locate the script element by ID
    const scriptToRemove = this.#document?.querySelector<HTMLScriptElement>(`script#${id}`);

    if (scriptToRemove) {
      // Safely remove the script element
      this.#renderer.removeChild(this.#document?.head, scriptToRemove);
    }
  }

  /**
   * Updates or creates a link element in the document's head
   * @param rel - Specifies the relationship between the document and the linked resource
   * @param href - The URL of the linked resource
   */
  setLinkTag(rel: string, href: string): void {
    // Validate input to prevent empty or invalid values
    if (!rel || !rel.trim()) {
      throw new Error(`Invalid rel attribute: "${rel}"`);
    }
    if (!href || !href.trim()) {
      throw new Error(`Invalid href attribute: "${href}"`);
    }

    // Check if a link element with the same "rel" already exists
    const existingLink = this.#document?.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (existingLink && !isRelAllowedMultipleTimes(rel)) {
      // If link exists, update the "href" attribute
      // This avoids creating duplicate link tags
      this.#renderer.setAttribute(existingLink, 'href', href);
    } else {
      // If link does not exist, create a new <link> element
      const newLink = this.#renderer.createElement('link') as HTMLLinkElement;
      this.#renderer.setAttribute(newLink, 'rel', rel);
      this.#renderer.setAttribute(newLink, 'href', href);

      // Safely append the new link element to the <head>
      const headElement = this.#document?.head;
      if (!headElement) {
        throw new Error('Unable to access the document head.');
      }

      this.#renderer.appendChild(headElement, newLink);
    }
  }

  /**
   * Removes a link element from the document's head by its "rel" attribute.
   *
   * @param rel - The "rel" attribute of the link tag to remove.
   */
  removeLinkTag(rel: string): void {
    // Validate input to ensure it's not empty
    if (!rel || !rel.trim()) {
      throw new Error(`Invalid rel attribute: "${rel}"`);
    }

    const linkElement = this.#document?.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    // If the link exists, remove it from the document head
    if (linkElement) {
      this.#renderer.removeChild(this.#document.head, linkElement);
    }
  }

  #updateMetaTag(seo: Seo): void {
    this.#metaService.updateTag({
      name: 'description',
      content: seo.description,
    });
    this.#metaService.updateTag({
      name: 'keywords',
      content: seo.keywords,
    });
  }
}
